import type {ComponentProps} from 'react';

import {Icon, Tooltip, view} from '@sail/ui';
import {warning} from '@sail/icons/react/Icon';

import type {Conversation} from '@stripe-internal/conversations-client';

import {ChatButtonOption} from './ChatButtonOption';

type Props = {
  options: ComponentProps<typeof ChatButtonOption>['option'][];
  selectedOptionId?: string;
  recommendedOptionId?: string;
  disabled?: boolean;
  error?: React.ReactNode;
  animated?: boolean;
  conversation?: Conversation;
};

const ErrorMessage = ({children}: {children: React.ReactNode}): JSX.Element => {
  return (
    <Tooltip
      placement="left"
      delay={0}
      trigger={
        <Icon
          icon={warning}
          aria-label="warning"
          data-testid="error-tooltip-icon"
          css={{
            fill: 'red',
            alignSelf: 'flex-end',
          }}
        />
      }
    >
      {children}
    </Tooltip>
  );
};

export const ChatButtonOptions = ({
  options,
  selectedOptionId,
  recommendedOptionId,
  disabled,
  error,
  animated,
  conversation,
}: Props) => {
  const buttons = options.map((option, index) => (
    <ChatButtonOption
      key={`button-option-${option.value}`}
      index={index}
      selected={option.value === selectedOptionId}
      recommended={option.value === recommendedOptionId}
      disabled={!!selectedOptionId}
      hidden={!!selectedOptionId && option.value !== selectedOptionId}
      option={option}
      animated={animated}
      conversation={conversation}
      onPress={() => {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        option.onPress && option.onPress();
      }}
    />
  ));

  return (
    <view.div
      data-testid="button-options-container"
      className="button-options-container"
      css={{
        stack: 'y',
        justifyContent: 'unset',
        alignItems: 'flex-end',
        gap: 'small',
        contain: 'layout',
      }}
    >
      {disabled && !selectedOptionId ? null : buttons}
      {error ? <ErrorMessage>{error}</ErrorMessage> : null}
    </view.div>
  );
};
